import React, {type PropsWithChildren, useMemo, use} from 'react';
import PopoutWindowPortal from './PopoutWindowPortal';
import {Box} from '@mui/material';
import {CustomMenuItem} from '../arr-menu/ArrMenu';
import {InPortal, createHtmlPortalNode} from 'react-reverse-portal';
import {panelContext} from './ArrPopoutPanel';
import RenderComponentWrapper from './RenderComponentWrapper';
import './ArrPopoutPanel.scss';

// Types Definitions
// ----------------------------------
export type PopoutPanelProps = PropsWithChildren<{
  width?: number;
  height?: number;
  title?: string;
  actionMenuItems?: CustomMenuItem[];
  onActionMenu?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
}>;

// ----------------------------------
// PopoutPanel Component
// ----------------------------------
const PopoutPanel: React.FC<PopoutPanelProps> = ({
  children,
  width,
  height,
  title = 'Popout Window',
  actionMenuItems = [],
  onActionMenu,
  onOpen,
  onClose,
  ...props
}) => {
  const portalNode = useMemo(() => createHtmlPortalNode(), []);
  const {isPopoutOpen, setIsPopoutOpen} = use(panelContext);
  // -------------------

  const togglePopout = () => {
    if (isPopoutOpen && onClose) {
      onClose();
    }

    if (!isPopoutOpen && onOpen) {
      onOpen();
    }
    setIsPopoutOpen(!isPopoutOpen);
  };
  // -------------------

  const handleMenu = () => {
    if (onActionMenu) {
      onActionMenu();
    }
  };
  // -------------------

  return (
    <Box className="arr-popout-panel">
      <InPortal node={portalNode}>{children}</InPortal>

      {isPopoutOpen ? (
        <PopoutWindowPortal width={width} height={height}>
          <RenderComponentWrapper
            title={title}
            portalNode={portalNode}
            actionMenuItems={actionMenuItems}
            isPopoutOpen={isPopoutOpen}
            onPopout={togglePopout}
            onActionMenu={handleMenu}
            {...props}
          />
        </PopoutWindowPortal>
      ) : (
        <RenderComponentWrapper
          title={title}
          portalNode={portalNode}
          actionMenuItems={actionMenuItems}
          isPopoutOpen={isPopoutOpen}
          onPopout={togglePopout}
          onActionMenu={handleMenu}
          {...props}
        />
      )}
    </Box>
  );
};

export {PopoutPanel};
export default PopoutPanel;
